import React, { useState, useRef } from 'react';
import { Play, Pause, RefreshCw, Music, Trash2, Upload, Loader2 } from 'lucide-react';
import { openRecordingStream } from '../../lib/recordings.js';
import OutboundVoicemailList, { useOutboundVoicemails } from './OutboundVoicemailList.jsx';

/**
 * VoicemailsTab — Tab managing inbound voicemails and pre-recorded voicemail drops.
 */
export default function VoicemailsTab({
  vmTab,
  setVmTab,
  inboxVMs = [],
  drops = [],
  vmUploadName,
  setVmUploadName,
  vmUploading,
  vmFileRef,
  handleVMUpload,
  deleteVM,
  refetchInbox,
  onOpenLead,
}) {
  const { data: outboundData } = useOutboundVoicemails();
  const outboundCount = outboundData?.data?.voicemails?.length || 0;
  const [playingId, setPlayingId] = useState(null);
  const audioRef = useRef(null);

  async function playVM(id) {
    if (playingId === id) {
      audioRef.current?.pause();
      setPlayingId(null);
      return;
    }
    if (audioRef.current) audioRef.current.pause();
    const url = await openRecordingStream(id);
    if (!url) {
      setPlayingId(null);
      return;
    }
    const audio = new Audio(url);
    audio.onended = () => setPlayingId(null);
    audio.play().catch(() => setPlayingId(null));
    audioRef.current = audio;
    setPlayingId(id);
  }

  return (
    <div className="p-4 space-y-3">
      {/* Sub-tabs */}
      <div className="flex rounded-lg overflow-hidden border border-civix-border">
        <button
          onClick={() => setVmTab('inbox')}
          className={`flex-1 py-2 text-xs font-semibold transition ${vmTab === 'inbox' ? 'bg-civix-purple text-white' : 'text-white/50 hover:text-white bg-civix-panelLight'}`}
        >
          Inbox ({inboxVMs.length})
        </button>
        <button
          onClick={() => setVmTab('outbound')}
          className={`flex-1 py-2 text-xs font-semibold transition ${vmTab === 'outbound' ? 'bg-civix-purple text-white' : 'text-white/50 hover:text-white bg-civix-panelLight'}`}
        >
          Outbound ({outboundCount})
        </button>
        <button
          onClick={() => setVmTab('drops')}
          className={`flex-1 py-2 text-xs font-semibold transition ${vmTab === 'drops' ? 'bg-civix-purple text-white' : 'text-white/50 hover:text-white bg-civix-panelLight'}`}
        >
          My Drops ({drops.length})
        </button>
      </div>

      {vmTab === 'inbox' && (
        <div className="space-y-2">
          {inboxVMs.length === 0 ? (
            <div className="text-center py-8 text-white/30 text-sm">No inbound voicemails</div>
          ) : (
            inboxVMs.map((vm) => (
              <div key={vm._id} className="bg-civix-panelLight rounded-xl p-3 border border-civix-border/60">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-white text-sm font-medium">
                    {vm.fromNumber || 'Unknown'}
                  </span>
                  <span className="text-white/40 text-[10px]">
                    {vm.recordingDurationSec ? `${vm.recordingDurationSec}s` : ''}
                  </span>
                </div>
                {vm.recordingUrl && (
                  <button
                    onClick={() => playVM(vm._id)}
                    className="flex items-center gap-2 text-civix-purple text-xs hover:text-civix-purpleSoft transition"
                  >
                    {playingId === vm._id ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                    {playingId === vm._id ? 'Pause' : 'Play Recording'}
                  </button>
                )}
              </div>
            ))
          )}
          <button onClick={refetchInbox} className="text-civix-purple text-xs flex items-center gap-1 hover:text-civix-purpleSoft transition">
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      )}

      {vmTab === 'outbound' && <OutboundVoicemailList onOpenLead={onOpenLead} />}

      {vmTab === 'drops' && (
        <div className="space-y-2">
          {drops.length === 0 ? (
            <div className="text-center py-4 text-white/30 text-sm">No voicemail drops saved</div>
          ) : (
            drops.map((vm) => (
              <div key={vm.id} className="bg-civix-panelLight rounded-xl p-3 border border-civix-border/60 flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-civix-purple/20 flex items-center justify-center shrink-0">
                  <Music className="w-4 h-4 text-civix-purple" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-xs font-medium truncate">{vm.name}</div>
                  <div className="text-white/40 text-[10px]">{vm.durationSec ? `${vm.durationSec}s` : 'No duration'}</div>
                </div>
                <button onClick={() => deleteVM(vm.id)} className="text-white/30 hover:text-red-400 transition shrink-0">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))
          )}

          {/* Upload new drop */}
          <div className="bg-civix-panelLight/50 rounded-xl p-3 border border-civix-border/40 space-y-2">
            <div className="text-[10px] uppercase tracking-wider text-white/40">Upload New Drop</div>
            <input
              type="text"
              value={vmUploadName}
              onChange={(e) => setVmUploadName(e.target.value)}
              placeholder="Name (e.g., Left Voicemail 1)"
              className="w-full bg-civix-panel border border-civix-border rounded-lg px-2.5 py-2 text-xs text-white placeholder:text-white/30 focus:outline-hidden focus:border-civix-purple transition"
            />
            <input
              ref={vmFileRef}
              type="file"
              accept="audio/*"
              className="w-full text-xs text-white/50 file:mr-2 file:py-1 file:px-2 file:rounded-sm file:border-0 file:bg-civix-border file:text-white/70 file:text-xs"
            />
            <button
              onClick={handleVMUpload}
              disabled={vmUploading}
              className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg bg-civix-purple hover:bg-civix-purpleDeep disabled:opacity-50 text-white text-xs font-semibold transition"
            >
              {vmUploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
              {vmUploading ? 'Uploading…' : 'Upload Drop'}
            </button>
            <div className="text-[10px] text-white/30">Max 1 MB · mp3, wav, ogg</div>
          </div>
        </div>
      )}
    </div>
  );
}
